import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { DEFAULT_BACKDROP_OVERLAY } from '@/theme/themes';

/**
 * One-time welcome popup shown to a new user on the FIRST Workout page visit
 * after their program has been generated. Gated by a server-side flag
 * (`welcome_popup_seen`) so it never fires again on any device.
 *
 * Single column over the active theme's carbon backdrop. Each point is a glass
 * row whose left spine lights as its line types. No video, no imagery; every
 * colour comes from the theme accent tokens.
 */

// Each feature has segments so we can render key words in the accent colour
// mid-sentence while the typewriter still types character-by-character.
const FEATURES = [
  [
    { t: 'Your ', hl: false },
    { t: 'intelligent program', hl: true },
    { t: ' is built — we\u2019ve unlocked ', hl: false },
    { t: 'Day 1', hl: true },
    { t: ' so you can dive straight in.', hl: false },
  ],
  [
    { t: 'Every exercise was ', hl: false },
    { t: 'hand-picked', hl: true },
    { t: ' for your goals, equipment and experience.', hl: false },
  ],
  [
    { t: 'Ease in gently \u2014 ', hl: false },
    { t: 'Week 1 is Calibration Week', hl: true },
    { t: ': we assess, learn and adapt as you move.', hl: false },
  ],
  [
    { t: 'Not vibing with an exercise? ', hl: false },
    { t: 'Swap it instantly', hl: true },
    { t: ' from our smart library or your custom picks.', hl: false },
  ],
  [
    { t: 'Prefer to design your own plan? Switch to ', hl: false },
    { t: 'Custom Build', hl: true },
    { t: ' anytime from Program \u2192 Custom Build.', hl: false },
  ],
];

const CHAR_MS = 22;
const LINE_GAP_MS = 420;
const START_DELAY_MS = 900;
const CTA_UNLOCK_MS = 3000; // Start Training becomes tappable after 3s

const TOTAL_CHARS = FEATURES.reduce(
  (n, segs) => n + segs.reduce((s, seg) => s + seg.t.length, 0),
  0
);

/** One feature row: glass panel with a spine that lights as the line types. */
function FeatureLine({ index, segments, charsTyped, lit }) {
  let remaining = charsTyped;
  const rendered = segments.map((seg, si) => {
    const show = Math.max(0, Math.min(seg.t.length, remaining));
    remaining -= seg.t.length;
    if (show <= 0) return null;
    return (
      <span key={si} style={seg.hl ? { color: 'var(--accent-hex)', fontWeight: 700 } : undefined}>
        {seg.t.slice(0, show)}
      </span>
    );
  });

  return (
    <div
      className="relative flex items-start gap-3 rounded-xl border py-2.5 pl-4 pr-3 sm:py-3 sm:pl-5 transition-colors duration-500"
      style={{
        background: lit
          ? 'linear-gradient(90deg, rgba(var(--accent-rgb),0.10), rgba(255,255,255,0.02))'
          : 'rgba(255,255,255,0.02)',
        borderColor: lit ? 'rgba(var(--accent-rgb),0.30)' : 'rgba(255,255,255,0.07)',
      }}
      data-testid={`welcome-feature-${index}`}
    >
      {/* Spine */}
      <span
        aria-hidden
        className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full transition-all duration-500"
        style={{
          background: lit ? 'var(--accent-hex)' : 'rgba(255,255,255,0.12)',
          boxShadow: lit ? '0 0 14px rgba(var(--accent-rgb),0.75)' : 'none',
        }}
      />
      <span
        aria-hidden
        className="mt-0.5 shrink-0 font-heading font-black text-xs sm:text-sm tabular-nums transition-colors duration-500"
        style={{ color: lit ? 'var(--accent-hex)' : 'rgba(255,255,255,0.3)' }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <p className="text-[13px] sm:text-lg leading-[1.35] sm:leading-relaxed text-white/90 font-medium">
        {rendered}
      </p>
    </div>
  );
}

export default function WelcomePopup({ open, onDismiss }) {
  const { activeTheme } = useTheme();
  const [progress, setProgress] = useState(-1);
  const [completed, setCompleted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const skippedRef = useRef(false);

  const bounds = useMemo(() => {
    const acc = [];
    let running = 0;
    FEATURES.forEach(segs => {
      const len = segs.reduce((s, seg) => s + seg.t.length, 0);
      acc.push({ start: running, len });
      running += len;
    });
    return acc;
  }, []);

  useEffect(() => {
    if (!open) {
      setProgress(-1);
      setCompleted(false);
      setUnlocked(false);
      skippedRef.current = false;
      return;
    }
    let cancelled = false;
    const unlockTimer = setTimeout(() => { if (!cancelled) setUnlocked(true); }, CTA_UNLOCK_MS);
    const startTimer = setTimeout(() => {
      if (cancelled) return;
      setProgress(0);
      const tick = (p) => {
        if (cancelled || skippedRef.current) return;
        if (p >= TOTAL_CHARS) {
          setCompleted(true);
          return;
        }
        const inLine = bounds.find(b => p >= b.start && p < b.start + b.len);
        const isLineEnd = inLine && p + 1 === inLine.start + inLine.len;
        setProgress(p + 1);
        setTimeout(() => tick(p + 1), isLineEnd ? LINE_GAP_MS : CHAR_MS);
      };
      setTimeout(() => tick(0), CHAR_MS);
    }, START_DELAY_MS);
    return () => { cancelled = true; clearTimeout(startTimer); clearTimeout(unlockTimer); };
  }, [open, bounds]);

  const skipTypewriter = () => {
    if (completed) return;
    skippedRef.current = true;
    setProgress(TOTAL_CHARS);
    setCompleted(true);
  };

  // Lock the page behind the dialog, otherwise the wheel scrolls the Program
  // page underneath instead of the popup.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const overlay = activeTheme?.overlay || DEFAULT_BACKDROP_OVERLAY;
  const backdrop = activeTheme?.background
    ? { backgroundImage: `${overlay}, url(${activeTheme.background})`, backgroundSize: 'cover', backgroundPosition: 'center top' }
    : { background: '#0b0b0b' };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-black/85"
      data-testid="welcome-popup"
      role="dialog"
      aria-modal="true"
      style={{ WebkitTextSizeAdjust: '100%', textSizeAdjust: '100%' }}
    >
      <div
        className={
          'relative w-full max-w-2xl h-[100dvh] max-h-[100dvh] rounded-none ' +
          'sm:h-[min(88dvh,760px)] sm:max-h-[88dvh] sm:rounded-3xl overflow-hidden ' +
          'border-0 sm:border'
        }
        style={{
          ...backdrop,
          borderColor: 'rgba(var(--accent-rgb),0.35)',
          boxShadow: '0 0 60px rgba(var(--accent-rgb),0.22), 0 0 120px rgba(0,0,0,0.6)',
        }}
      >
        {/* Accent wash so the copy always has something to sit on */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 0% 0%, rgba(var(--accent-rgb),0.14), transparent 50%),' +
              'linear-gradient(180deg, rgba(8,8,8,0.35) 0%, rgba(8,8,8,0.72) 100%)',
          }}
        />

        <div className="relative z-10 flex h-full flex-col overflow-y-auto overscroll-contain px-5 pt-8 pb-3 sm:px-10 sm:pt-10 sm:pb-6">
          {/* Title */}
          <div className="shrink-0" data-testid="welcome-popup-title">
            <h1 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-4xl leading-[0.95] text-white">
              Welcome to <span style={{ color: 'var(--accent-hex)' }}>BodyMax</span>
            </h1>
            <p
              className="mt-1.5 text-[11px] sm:text-base font-heading font-semibold uppercase tracking-wider"
              style={{ color: 'rgba(var(--accent-rgb),0.85)' }}
            >
              {'Here\u2019s what to expect'}
            </p>
            <div
              className="mt-3 h-[2px] w-12 sm:w-16 rounded-full"
              style={{ background: 'var(--accent-hex)', boxShadow: '0 0 12px rgba(var(--accent-rgb),0.9)' }}
            />
          </div>

          {/* Feature rows */}
          <div
            className="mt-5 sm:mt-7 space-y-2 sm:space-y-3 shrink-0"
            onClick={skipTypewriter}
            data-testid="welcome-popup-features"
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') skipTypewriter(); }}
            title="Tap to reveal all"
          >
            {FEATURES.map((segs, i) => {
              const b = bounds[i];
              const chars = progress < 0 ? 0 : Math.max(0, Math.min(b.len, progress - b.start));
              if (progress < b.start && !completed) return null;
              return (
                <FeatureLine
                  key={i}
                  index={i}
                  segments={segs}
                  charsTyped={completed ? b.len : chars}
                  lit={completed || chars >= b.len}
                />
              );
            })}
          </div>

          {/* CTA — unlocks 3s after the popup opens */}
          <div
            className="mt-auto pt-4 shrink-0"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
          >
            <button
              onClick={onDismiss}
              disabled={!unlocked}
              data-testid="welcome-popup-cta"
              className="w-full sm:w-auto sm:px-12 h-12 rounded-full font-heading font-black text-sm sm:text-base uppercase tracking-wider transition-all duration-500"
              style={unlocked
                ? {
                    background: 'var(--accent-hex)',
                    color: 'hsl(var(--accent-foreground))',
                    boxShadow: '0 0 32px rgba(var(--accent-rgb),0.55)',
                  }
                : {
                    background: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'not-allowed',
                  }}
            >
              Start Training
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
